// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { useChangeRequestFieldOptionsQuery } from '../../../../queries/useConfigQueries';
import { useAttributeListQuery } from '../../../../features/configuration/attributeLibrary/api/attributeQueries';
import { getErrorHelperText } from '../../../../utils/Utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import { DRAFT_INPUT_SX } from './TextFieldComponent';

const getAttributeListOptionLabel = (option) => option?.label;
const isOptionEqualToValue = (option, value) => option?.label === value?.label;

const FieldChangeActionsComponent = ({
  label,
  titleLabel,
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  required = false,
  modelTemplateDetails = EMPTY_OBJECT,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  /**
   * for FieldChangeActions data type value will be
   * {"key": "description", "label":"Description", path:"attribute.common"}
   */
  const { data: changeRequestFieldOptions = EMPTY_ARRAY } =
    useChangeRequestFieldOptionsQuery();
  const { data: attributeList = EMPTY_ARRAY } = useAttributeListQuery();

  // Get the attribute ID that is mapped to ConfigurationChangeType from template
  const configurationChangeTypeAttributeId = useMemo(() => {
    return modelTemplateDetails?.change_request_attribute_mapping
      ?.ConfigurationChangeType;
  }, [modelTemplateDetails]);

  // Get the selected value of ConfigurationChangeType attribute from formik form values
  const configurationChangeTypeValue = useMemo(() => {
    return entityDetailsFormikForm?.values?.[
      configurationChangeTypeAttributeId
    ]?.toLowerCase();
  }, [configurationChangeTypeAttributeId, entityDetailsFormikForm?.values]);

  // get the attribute details of the attribute according to the selected value of ConfigurationChangeType attribute
  const attrubuteDetails = useMemo(() => {
    let trackAttribute = null;
    if (configurationChangeTypeValue === 'attribute') {
      trackAttribute =
        modelTemplateDetails?.change_request_attribute_mapping?.AttributeList;
    } else if (configurationChangeTypeValue === 'template') {
      trackAttribute =
        modelTemplateDetails?.change_request_attribute_mapping?.TemplateList;
    } else if (configurationChangeTypeValue === 'email template') {
      trackAttribute =
        modelTemplateDetails?.change_request_attribute_mapping
          ?.EmailTemplateList;
    } else if (configurationChangeTypeValue === 'parameter table') {
      trackAttribute =
        modelTemplateDetails?.change_request_attribute_mapping
          ?.ParameterTableList;
    }

    return attributeList.find(
      (option) => String(option?.id) === String(trackAttribute)
    );
  }, [attributeList, configurationChangeTypeValue, modelTemplateDetails]);

  /**
   * if configurationChangeTypeValue is "attribute" then options will be depend on its data type of its valus also
   */
  const selectedValueInsideTrackedAttribute = useMemo(() => {
    if (configurationChangeTypeValue === 'attribute') {
      /**
       * for AttributeList data type value will be
       * {"id": 876, "label":"attr type list"}
       */
      const attValue = entityDetailsFormikForm?.values?.[attrubuteDetails?.id];
      return attributeList.find(
        (option) => String(option?.id) === String(attValue?.id)
      );
    }
    return null;
  }, [
    configurationChangeTypeValue,
    attrubuteDetails,
    attributeList,
    entityDetailsFormikForm?.values,
  ]);

  const fieldChangeActionsOptions = useMemo(() => {
    if (
      !configurationChangeTypeValue ||
      !changeRequestFieldOptions?.[configurationChangeTypeValue]
    ) {
      return [];
    }
    const categoryOptions =
      changeRequestFieldOptions[configurationChangeTypeValue];

    const options = [...(categoryOptions?.common || [])];

    // Include data-type-specific options only when configurationChangeTypeValue is "attribute"
    if (configurationChangeTypeValue === 'attribute') {
      const attributeDataType = selectedValueInsideTrackedAttribute?.data_type;
      if (attributeDataType && categoryOptions?.[attributeDataType]) {
        options.push(...categoryOptions[attributeDataType]);
      }
    }
    return options;
  }, [
    configurationChangeTypeValue,
    changeRequestFieldOptions,
    selectedValueInsideTrackedAttribute,
  ]);

  const selectedOption = useMemo(() => {
    return (
      fieldChangeActionsOptions.find(
        (option) => option?.label === value?.label
      ) || null
    );
  }, [fieldChangeActionsOptions, value]);

  const handleChange = useCallback(
    (_event, newValue) => {
      if (newValue) {
        onChange(newValue);
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  const handleClose = useCallback(() => {
    onDraftBlur?.();
  }, [onDraftBlur]);

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        required={required}
        placeholder="Select Field Actions"
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors?.[attribute?.id],
            entityDetailsFormikForm?.touched?.[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors?.[attribute?.id],
          entityDetailsFormikForm?.touched?.[attribute?.id]
        )}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params?.slotProps?.input,
            placeholder: 'Select Field Actions',
          },

          inputLabel: {
            ...params?.slotProps?.inputLabel,
            shrink: true,
          },
        }}
      />
    ),
    [
      attribute?.id,
      entityDetailsFormikForm,
      label,
      required,
      titleLabel,
      hasDraftChip,
    ]
  );

  return (
    <Autocomplete
      value={selectedOption}
      options={fieldChangeActionsOptions}
      onChange={handleChange}
      onClose={handleClose}
      noOptionsText="No attributes present."
      popupIcon={<DropdownArrow />}
      getOptionLabel={getAttributeListOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={renderInput}
    />
  );
};

FieldChangeActionsComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data_type: PropTypes.string,
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    values: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({}),
      ])
    ),
    setFieldTouched: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
    touched: PropTypes.objectOf(PropTypes.bool),
  }),
  modelTemplateDetails: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.oneOf([null]),
  ]),
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default FieldChangeActionsComponent;
